// Drawing Guess in the world: the live drawing on the easel (a CanvasTexture
// everyone sees), the hint / status / timer over the frame, the scoreboard on
// the right, the guess feed on the left. In VR with controllers the drawer can
// also draw straight onto the easel: point at it and hold the trigger.
// Table-local, floor origin (see GameTable3D).

import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import type { ThreeEvent } from '@react-three/fiber';
import { noRaycast, vrColors as colors } from '../../VR/vrUi';
import { handMode } from '../../VR/hands/handTracking';
import { useCountdown } from '../kit/LobbyKit';
import { BoardPainter, getBoard, penFor, useDrawingSync } from './strokes';
import { BOARD_CY, BOARD_H, BOARD_TOP, BOARD_W, BOARD_Z, FRAME_W } from './easelLayout';
import { chatLineText, hintText, phaseDeadline, publicStatus, standings, type DrawState } from './drawingView';
import type { Table3DProps } from '../types';

const TEX_W = 1200, TEX_H = 800;
const _v = new THREE.Vector3();

/** A drawing surface showing table `tableId`'s board (shared by the easel and the VR pad). */
export function useBoardTexture(tableId: string | null, w = TEX_W, h = TEX_H) {
  const { canvas, texture, painter } = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    return { canvas, texture, painter: new BoardPainter(canvas) };
  }, [w, h]);
  useEffect(() => () => texture.dispose(), [texture]);
  useEffect(() => { painter.paint(getBoard(tableId ?? ''), true); texture.needsUpdate = true; }, [tableId, painter, texture]);
  useFrame(() => {
    if (!tableId) return;
    if (painter.paint(getBoard(tableId))) texture.needsUpdate = true;
  });
  return { canvas, texture };
}

/**
 * Pointer drawing on a plane mesh of size w × h (centred): controllers' rays
 * (trigger) in VR. Tracked hands draw with the fingertip instead (VRDrawPad),
 * so hand rays / touch pointers are ignored here.
 */
export function usePlanePen(worldId: string, tableId: string | null, enabled: boolean, w: number, h: number) {
  const mesh = useRef<THREE.Mesh>(null);
  const active = useRef<number | null>(null);
  const toUV = (e: ThreeEvent<PointerEvent>) => {
    const m = mesh.current!;
    m.worldToLocal(_v.copy(e.point));
    return [THREE.MathUtils.clamp(_v.x / w + 0.5, 0, 1), THREE.MathUtils.clamp(0.5 - _v.y / h, 0, 1)] as const;
  };
  const ok = (e: ThreeEvent<PointerEvent>) => enabled && !!tableId && !!mesh.current && ((e as any).pointerType ?? (e.nativeEvent as any)?.pointerType) === 'ray' && !handMode.active;
  const pen = tableId ? penFor(worldId, tableId) : null;
  useEffect(() => () => { if (active.current !== null) pen?.up(); active.current = null; }, [pen, enabled]);
  if (!enabled || !pen) return { mesh, handlers: {} };
  return {
    mesh,
    handlers: {
      onPointerDown: (e: ThreeEvent<PointerEvent>) => {
        if (!ok(e)) return;
        e.stopPropagation();
        try { (e.target as any).setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
        active.current = e.pointerId;
        const [x, y] = toUV(e);
        pen.down(x, y);
      },
      onPointerMove: (e: ThreeEvent<PointerEvent>) => {
        if (active.current !== e.pointerId || !mesh.current) return;
        e.stopPropagation();
        const [x, y] = toUV(e);
        pen.move(x, y);
      },
      onPointerUp: (e: ThreeEvent<PointerEvent>) => {
        if (active.current !== e.pointerId) return;
        e.stopPropagation();
        try { (e.target as any).releasePointerCapture?.(e.pointerId); } catch { /* released */ }
        active.current = null;
        pen.up();
      },
      onClick: (e: ThreeEvent<MouseEvent>) => e.stopPropagation(),
    },
  };
}

function Countdown({ deadline, position, size }: { deadline: number | null; position: [number, number, number]; size: number }) {
  const left = useCountdown(deadline);
  if (left === null) return null;
  return (
    <Text position={position} fontSize={size} color={left <= 10 ? '#ff9b8a' : '#ffffff'} outlineWidth={size * 0.07} outlineColor="#000" raycast={noRaycast} anchorX="right" anchorY="bottom">
      {`⏱ ${left}s`}
    </Text>
  );
}

export function DrawingTable3D({ worldId, table, seat }: Table3DProps) {
  const tableId = table?.id ?? null;
  useDrawingSync(worldId, table, seat);
  const { texture } = useBoardTexture(tableId);
  const presenting = useXR((s) => !!s.session);
  const st = table?.state;
  const g = (st?.g ?? null) as DrawState | null;
  const drawing = !!g && st?.phase === 'playing' && g.phase === 'drawing';
  const amDrawer = drawing && g!.drawer === seat;
  const { mesh, handlers } = usePlanePen(worldId, tableId, presenting && amDrawer, BOARD_W, BOARD_H);

  const headerY = BOARD_TOP + 0.16;
  const hint = g && st?.phase === 'playing' && (g.phase === 'drawing' || g.phase === 'reveal') && g.hint.length ? hintText(g.hint) : '';
  const rows = st && g ? standings(st).slice(0, 8) : [];
  const feed = g ? g.chat.slice(-6) : [];
  const roundLine = g && st?.phase === 'playing' ? `Round ${g.round}/${g.rounds}` : '';

  return (
    <>
      {/* The live drawing over the easel's board. */}
      <mesh ref={mesh} position={[0, BOARD_CY, BOARD_Z + 0.004]} name="drawing-easel-surface"
        raycast={presenting && amDrawer ? THREE.Mesh.prototype.raycast : (noRaycast as any)} {...handlers}>
        <planeGeometry args={[BOARD_W, BOARD_H]} />
        <meshBasicMaterial map={texture} toneMapped={false} />
      </mesh>

      {/* Over the frame: hint (or status), round, timer. */}
      <group position={[0, headerY, BOARD_Z]}>
        <Text position={[0, 0.2, 0]} fontSize={0.13} color="#ffffff" outlineWidth={0.008} outlineColor="#000" raycast={noRaycast} anchorY="bottom" maxWidth={FRAME_W}>
          {!st ? 'Drawing Guess' : hint || publicStatus(st)}
        </Text>
        <Text position={[-FRAME_W / 2, 0.02, 0]} fontSize={0.075} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorX="left" anchorY="bottom">
          {hint ? `✏ ${publicStatus(st!)}` : st?.phase === 'lobby' ? `✏ Drawing Guess · ${st.seats.filter(Boolean).length} joined` : '✏ Drawing Guess'}
        </Text>
        {roundLine && (
          <Text position={[0.15, 0.02, 0]} fontSize={0.075} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="bottom">
            {roundLine}
          </Text>
        )}
        {st && <Countdown deadline={phaseDeadline(st)} position={[FRAME_W / 2, 0.02, 0]} size={0.085} />}
      </group>

      {/* Scoreboard (right) */}
      {rows.length > 0 && (
        <group position={[FRAME_W / 2 + 0.2, BOARD_TOP - 0.05, BOARD_Z]}>
          <Text fontSize={0.075} color={colors.teal} outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorX="left" anchorY="top">Scores</Text>
          {rows.map((r, i) => (
            <Text key={r.seat} position={[0, -0.11 - i * 0.085, 0]} fontSize={0.062} color={r.seat === seat ? colors.teal : '#ffffff'}
              outlineWidth={0.004} outlineColor="#000" raycast={noRaycast} anchorX="left" anchorY="top" maxWidth={1.1}>
              {`${r.drawer ? '✏ ' : r.guessed ? '✓ ' : ''}${r.name.slice(0, 16)}  ${r.score}${r.turn ? ` (+${r.turn})` : ''}`}
            </Text>
          ))}
        </group>
      )}

      {/* Guess feed (left): wrong guesses, "X guessed it!", table events. */}
      {feed.length > 0 && (
        <group position={[-FRAME_W / 2 - 0.2, BOARD_TOP - 0.05, BOARD_Z]}>
          {feed.map((line, i) => (
            <Text key={line.n} position={[0, -i * 0.085, 0]} fontSize={0.058}
              color={line.kind === 'correct' ? '#7ef0b0' : line.kind === 'sys' ? '#ffd27a' : '#ffffff'}
              outlineWidth={0.004} outlineColor="#000" raycast={noRaycast} anchorX="right" anchorY="top" maxWidth={1.2} textAlign="right">
              {chatLineText(line).slice(0, 48)}
            </Text>
          ))}
        </group>
      )}
    </>
  );
}

export default DrawingTable3D;
